import { useOnboardingValues } from "@mirage/service-onboarding/hooks";
import Sentry from "@mirage/shared/sentry";
import { useCallback, useEffect, useState } from "react";
import { useDashTeamInfo } from "./useDashTeamInfo";
import { useOnboardingSurveys } from "./useOnboardingSurveys";

import type { OnboardingSurveyId } from "@mirage/service-onboarding-surveys/service";
import type { OnboardingSurveyAnswer } from "@mirage/service-onboarding-surveys/service/types";
import type { OnboardingStorageSSOnboardingState } from "@mirage/service-onboarding/service";

const ONBOARDING_STATE_KEY = "selfServeOnboardingState";
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 5000;

export type SelfServeOnboardingState = OnboardingStorageSSOnboardingState & {
  surveyResponses: Map<OnboardingSurveyId, OnboardingSurveyAnswer[]>;
};

export const useSelfServeOnboardingState = () => {
  const { submitSurvey } = useOnboardingSurveys();
  const { getOnboardingValue, setOnboardingValue } = useOnboardingValues();
  const { teamName, teamLogoUrl, updateTeamLogo, updateTeamName } =
    useDashTeamInfo();

  const [companyName, setCompanyName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [companyLogo, setCompanyLogo] = useState("");
  const [surveyResponses, setSurveyResponses] = useState<
    Map<OnboardingSurveyId, OnboardingSurveyAnswer[]>
  >(new Map());

  const getOnboardingState = useCallback(async () => {
    const onboardingState = await getOnboardingValue(ONBOARDING_STATE_KEY);
    if (!onboardingState) return {} as SelfServeOnboardingState;
    let surveyResponsesMap = new Map();
    if (onboardingState.surveyResponsesStr) {
      surveyResponsesMap = new Map(
        JSON.parse(onboardingState.surveyResponsesStr),
      );
    }
    const state: SelfServeOnboardingState = {
      ...onboardingState,
      surveyResponses: surveyResponsesMap,
    };

    return state;
  }, [getOnboardingValue]);

  useEffect(() => {
    const loadOnboardingState = async () => {
      setLoading(true);
      const onboardingState = await getOnboardingState();

      setCompanyName(onboardingState.companyName ?? "");
      setCompanyLogo(onboardingState.companyLogo ?? "");
      setLoading(false);
      setSurveyResponses(onboardingState.surveyResponses ?? new Map());
    };

    loadOnboardingState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCompanyName((prev) => prev ?? teamName ?? "");
  }, [teamName]);

  useEffect(() => {
    setCompanyLogo((prev) => prev ?? teamLogoUrl ?? "");
  }, [teamLogoUrl]);

  const saveOnboardingState = async () => {
    setIsSaving(true);
    await saveCompanyInfo();
    // submit team specific profiling survey responses
    await Promise.allSettled(
      Array.from(surveyResponses.entries()).map(([surveyId, answers]) =>
        submitSurvey(answers, surveyId),
      ),
    );
    setIsSaving(false);
  };

  const saveCompanyInfo = async () => {
    // Intentionally not waiting because this could be an extended period of time due to retries
    saveCompanyLogo();
    saveCompanyName();
  };

  const saveCompanyLogo = async () => {
    if (companyLogo)
      await withRetry("saveCompanyLogo", () => updateTeamLogo(companyLogo));
  };

  const saveCompanyName = async () => {
    if (companyName)
      await withRetry("saveCompanyName", () => updateTeamName(companyName));
  };

  // Saving the company info can fail due race condition with subscription
  const withRetry = async (
    functionName: string,
    fn: () => Promise<boolean>,
  ) => {
    for (let attempts = 0; attempts < MAX_RETRIES; attempts++) {
      if (await fn()) {
        return;
      }
      if (attempts < MAX_RETRIES - 1)
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
    }
    Sentry.captureMessage(
      `[useSelfServeOnboardingState] Attempted ${functionName} ${MAX_RETRIES} times and failed`,
    );
  };

  const storeCompanyLogo = async (companyLogo: string) => {
    const onboardingState = await getOnboardingState();
    onboardingState.companyLogo = companyLogo;
    await storeOnboardingState(onboardingState);
    setCompanyLogo(companyLogo);
  };

  const storeCompanyName = async (companyName: string) => {
    const onboardingState = await getOnboardingState();
    onboardingState.companyName = companyName;
    await storeOnboardingState(onboardingState);
    setCompanyName(companyName);
  };

  const storeSurveyResponses = async (
    surveyName: OnboardingSurveyId,
    surveyResponses: OnboardingSurveyAnswer[],
  ) => {
    const onboardingState = await getOnboardingState();
    if (!onboardingState.surveyResponses) {
      onboardingState.surveyResponses = new Map<
        OnboardingSurveyId,
        OnboardingSurveyAnswer[]
      >();
    }
    onboardingState.surveyResponses.set(surveyName, surveyResponses);
    await storeOnboardingState(onboardingState);
    setSurveyResponses(onboardingState.surveyResponses);
  };

  const storeOnboardingState = async (
    onboardingState: SelfServeOnboardingState,
  ) => {
    let surveyResponsesStr = "";
    if (onboardingState.surveyResponses) {
      surveyResponsesStr = JSON.stringify(
        Array.from(onboardingState.surveyResponses.entries()),
      );
    }

    return await setOnboardingValue(ONBOARDING_STATE_KEY, {
      ...onboardingState,
      surveyResponsesStr,
    });
  };

  return {
    isSaving,
    loading,
    companyLogo,
    companyName,
    surveyResponses,
    saveOnboardingState,
    setCompanyLogo: storeCompanyLogo,
    setCompanyName: storeCompanyName,
    setSurveyResponses: storeSurveyResponses,
  };
};
